import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { registerEvent, enrolCourse, bookStudio } from "@/lib/api";
import { startPayment } from "@/lib/razorpay";

/**
 * Generic booking / enrolment / registration dialog with optional Razorpay payment.
 * Props:
 *  - type: "event" | "course" | "studio"
 *  - item: content object (event/course) OR studio offering
 *  - open, setOpen
 */
export const BookingDialog = ({ type, item, open, setOpen }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", participants: 1, preferred_date: "", message: "" });
  const [loading, setLoading] = useState(false);

  if (!item) return null;

  const isPaid =
    type === "event" ? !item.is_free && item.price > 0
    : type === "course" ? item.kind === "paid"
    : type === "studio" ? (item.price_from || 0) > 0
    : false;

  const priceValue = type === "studio" ? item.price_from : item.price;
  const title = item.title;

  const meta = {
    event: {
      heading: `Register for ${title}`,
      desc: item.is_free ? "This experience is free. Reserve your spot below." : "Reserve your spot below.",
      cta: isPaid ? `Pay & Register — ₹${priceValue}` : "Reserve Spot",
      slug: item.slug,
      itemType: "event",
    },
    course: {
      heading: `Enrol in ${title}`,
      desc: item.kind === "free" ? "This is a free workbook. Enter your details to get access." : "Complete your enrolment below.",
      cta: isPaid ? `Enrol — ₹${priceValue}` : "Get Free Access",
      slug: item.slug,
      itemType: "course",
    },
    studio: {
      heading: `Book: ${title}`,
      desc: "Send a booking request. We'll confirm availability and details.",
      cta: isPaid ? `Book — from ₹${priceValue}` : "Request Booking",
      slug: item.key,
      itemType: "studio",
    },
  }[type];

  const submitLead = async () => {
    if (type === "event") {
      return registerEvent(meta.slug, {
        name: form.name, email: form.email, phone: form.phone,
        participants: Number(form.participants) || 1, message: form.message,
      });
    }
    if (type === "course") {
      return enrolCourse(meta.slug, { name: form.name, email: form.email, phone: form.phone, message: form.message });
    }
    return bookStudio({
      name: form.name, email: form.email, phone: form.phone,
      offering_key: item.key, preferred_date: form.preferred_date, message: form.message,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Always capture the lead first so nothing is lost if payment fails.
      const res = await submitLead();

      if (!isPaid) {
        toast.success(res.message || "Done!");
        setOpen(false);
        return;
      }

      const pay = await startPayment({
        item_type: meta.itemType,
        item_slug: meta.slug,
        name: form.name,
        email: form.email,
        phone: form.phone,
      });

      if (pay.status === "paid") {
        toast.success("Payment successful — you're all set!");
        setOpen(false);
      } else if (pay.status === "not_configured") {
        toast.success("Request saved! Online payments aren't live yet — we'll reach out with payment details.");
        setOpen(false);
      } else if (pay.status === "cancelled") {
        toast.info("Payment cancelled. Your request is saved — you can pay later or we'll follow up.");
        setOpen(false);
      } else {
        toast.error(pay.message || "Payment could not be completed, but your request is saved.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-card max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{meta.heading}</DialogTitle>
          <DialogDescription>{meta.desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bk-name">Name</Label>
            <Input id="bk-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="booking-name-input" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="bk-email">Email</Label>
              <Input id="bk-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="booking-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bk-phone">Phone (optional)</Label>
              <Input id="bk-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" data-testid="booking-phone-input" />
            </div>
          </div>

          {type === "event" && (
            <div className="space-y-2">
              <Label htmlFor="bk-participants">Number of participants</Label>
              <Input id="bk-participants" type="number" min={1} value={form.participants} onChange={(e) => setForm({ ...form, participants: e.target.value })} data-testid="booking-participants-input" />
            </div>
          )}

          {type === "studio" && (
            <div className="space-y-2">
              <Label htmlFor="bk-date">Preferred date / time (optional)</Label>
              <Input id="bk-date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} placeholder="e.g. Sat 12 Apr, afternoon" data-testid="booking-date-input" />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="bk-msg">Message (optional)</Label>
            <Textarea id="bk-msg" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Anything you'd like us to know?" data-testid="booking-message-input" />
          </div>

          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="booking-submit-button">
            {loading ? "Please wait..." : meta.cta}
          </Button>
          {isPaid && (
            <p className="text-center text-xs text-muted-foreground">Secure payment via Razorpay. Your request is saved even if you pay later.</p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
